export enum MintStatus {
  IGNORED = "ignored",
  TO_BE_VALIDATED = "to-be-validated",
  TO_BE_MINTED = "to-be-minted",
  MINTING = "minting",
  MINTED = "minted",
  FAILED = "failed",
}

/**
 * Fleet vehicle status, as stored by the API (App\Models\Vehicle_status).
 *
 * WHY THIS IS NO LONGER AN ENUM
 * -----------------------------
 * The previous version was:
 *
 *   export enum VehicleStatus {
 *     "En commande" = "O",
 *     "Affecté"     = "A",
 *     "Libre"       = "F",
 *     ...
 *   }
 *
 * and `getKeyLabelByValue("F")` returned the enum KEY - the literal string
 * "Libre" - which was then rendered straight into the status chip. Display
 * copy was stored as a TypeScript identifier, so it could never be
 * translated: an English user saw "LIBRE" and "AFFECTÉ" no matter what
 * language they picked, because there was no translation lookup anywhere in
 * the path. It also travelled through the forms as a value: the status
 * <select> listed `Object.keys(VehicleStatus)`, formik held "Libre", and
 * `getValueByKeyVehicleStatus` mapped it back to "F" on submit - a
 * round-trip through a French UI string in the middle of a data pipeline.
 *
 * Codes are now the only thing that moves. Labels are looked up at RENDER
 * time through i18next, which is what makes them follow a live language
 * switch.
 */
export type VehicleStatusCode = "O" | "A" | "F" | "W";

export const VEHICLE_STATUS = {
  ON_ORDER: "O",
  ASSIGNED: "A",
  FREE: "F",
  WAITING_VALIDATION: "W",
} as const satisfies Record<string, VehicleStatusCode>;

/** Display order of the status <select> and legend. */
export const VEHICLE_STATUS_CODES: VehicleStatusCode[] = ["O", "A", "F", "W"];

/**
 * Code -> i18n key. The fallback is the ENGLISH string, never the French one:
 * a fallback is what ships when a key is missing from a locale file, so a
 * French fallback silently reintroduces the exact bug this file used to have.
 */
export const VEHICLE_STATUS_LABELS: Record<
  VehicleStatusCode,
  { key: string; fallback: string }
> = {
  O: { key: "vehicule.statusOnOrder", fallback: "On order" },
  A: { key: "vehicule.statusAffected", fallback: "Assigned" },
  F: { key: "vehicule.statusFree", fallback: "Available" },
  W: { key: "vehicule.statusWaiting", fallback: "Awaiting validation" },
};

/** Minimal shape of i18next's `t`, so callers can pass the one from
 *  useTranslation() without this module depending on react-i18next.
 *  The third argument carries interpolation values, e.g.
 *  t("event.openDocumentsCount", "Open {{count}} document(s)", { count }). */
export type TranslateFn = (
  key: string,
  fallback?: string,
  options?: Record<string, unknown>
) => string;

export const isVehicleStatusCode = (
  value: unknown
): value is VehicleStatusCode =>
  typeof value === "string" && value in VEHICLE_STATUS_LABELS;

/**
 * Translate a status code for display. Pass the `t` from useTranslation() -
 * NOT the i18n singleton - so the label re-renders on a language switch.
 *
 * Unknown or missing codes fall back to FREE, matching the API default.
 */
export const translateVehicleStatus = (
  t: TranslateFn,
  code: unknown
): string => {
  const entry = isVehicleStatusCode(code)
    ? VEHICLE_STATUS_LABELS[code]
    : VEHICLE_STATUS_LABELS.F;
  return t(entry.key, entry.fallback);
};

/** Options for CustomTextSelect: the label is translated, the value stays a
 *  code, so nothing downstream ever parses a human-readable string. */
export const getVehicleStatusOptions = (
  t: TranslateFn
): { label: string; value: VehicleStatusCode }[] =>
  VEHICLE_STATUS_CODES.map((code) => ({
    label: translateVehicleStatus(t, code),
    value: code,
  }));

/** Maps a status code to the CustomChip colour variant. */
export const getStatusFromValues = (
  value: string
): "COMMAND" | "AFFECTED" | "FREE" | "ACTIF" | "WAITING" => {
  switch (value) {
    case "O":
      return "COMMAND";

    case "A":
      return "AFFECTED";

    case "F":
      return "FREE";

    case "W":
      return "WAITING";

    default:
      return "FREE";
  }
};
